import {
  createInstance,
  ModuleFederation
} from '@module-federation/enhanced/runtime';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as axios from 'axios';
import * as muiStyles from '@mui/styles';
import * as EmotionReact from '@emotion/react';

// Define shared dependency structure
interface SharedDependency {
  singleton: boolean;
  lib: () => any;
  version: string;
}

interface SharedDependencies {
  [key: string]: SharedDependency;
}

// Define available modules in the scheduling UI
type SchedulingUiModules =
  | 'KyruusSchedulingUI'
  | 'KyruusManagementUI'
  | 'KyruusCancellationUI'
  | 'KyruusWaitlistUI';

/**
 * Global declaration for webpack injected dependency versions
 */
declare const __DEPENDENCY_VERSIONS__: {
  [key: string]: string;
};

/**
 * These are the dependencies shared between this providermatch-consumer application and
 * the imported scheduling MFE.
 * __DEPENDENCY_VERSIONS__ are injected via a custom build plugin
 */
export function getSchedulingUiSharedDependencies(): SharedDependencies {
  return {
    react: {
      singleton: true,
      lib: () => React,
      version: __DEPENDENCY_VERSIONS__['react']
    },
    '@emotion/react': {
      singleton: true,
      lib: () => EmotionReact,
      version: __DEPENDENCY_VERSIONS__['@emotion/react']
    },
    'react-dom': {
      singleton: true,
      lib: () => ReactDOM,
      version: __DEPENDENCY_VERSIONS__['react-dom']
    },
    axios: {
      singleton: true,
      lib: () => axios,
      version: __DEPENDENCY_VERSIONS__['axios']
    },
    '@mui/styles': {
      singleton: true,
      lib: () => muiStyles,
      version: __DEPENDENCY_VERSIONS__['@mui/styles']
    }
  };
}

/**
 * Load a module federation instance of the scheduling UI micro frontend
 * @param url The URL to the micro frontend manifest
 * @returns A configured module federation instance
 */
export function loadSchedulingUiMfe(url: string): ModuleFederation {
  const schedulingMf = createInstance({
    name: 'scheduling',
    remotes: [
      {
        name: 'scheduling',
        entry: url, // 'https://lcl.kyruus.com:4000/current/mf-manifest.json',
        alias: 'scheduling'
      }
    ]
  });

  schedulingMf.registerShared(getSchedulingUiSharedDependencies());

  return schedulingMf;
}

/**
 * Interface for the hook parameters
 */
export interface SchedulingModuleOptions {
  /** URL to the micro frontend manifest */
  url: string;
  /** Name of the module to load from the scheduling UI */
  moduleName: SchedulingUiModules;
}

/**
 * Hook for importing a scheduling module from the scheduling MFE. The modules are exported from
 * the scheduling-ui project
 * @param options The options for loading the module
 * @returns A lazy-loaded React component
 */
export function useSchedulingModule({
  url,
  moduleName
}: SchedulingModuleOptions): React.LazyExoticComponent<any> {
  // always use 'current' version for now
  const versionedUrl = url.replace('{version}', 'current');
  return React.useMemo(() => {
    const mf = loadSchedulingUiMfe(versionedUrl);
    return React.lazy((): Promise<any> => {
      let myComponent;
      switch (moduleName) {
        case 'KyruusCancellationUI':
          myComponent = mf.loadRemote('scheduling/KyruusCancellationUI');
          break;
        case 'KyruusManagementUI':
          myComponent = mf.loadRemote('scheduling/KyruusManagementUI');
          break;
        case 'KyruusSchedulingUI':
          myComponent = mf.loadRemote('scheduling/KyruusSchedulingUI');
          break;
        case 'KyruusWaitlistUI':
          myComponent = mf.loadRemote('scheduling/KyruusWaitlistUI');
          break;
      }
      return myComponent;
    });
  }, [versionedUrl, moduleName]);
}
